import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Video,
  Building,
  Filter,
  Loader2,
  QrCode,
  CheckCircle,
  XCircle,
  Download,
  Bell,
  ExternalLink
} from 'lucide-react';

// Icono de Google Calendar
const GoogleCalendarIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M19.5 3h-3V1.5h-1.5V3h-6V1.5H7.5V3h-3C3.675 3 3 3.675 3 4.5v15c0 .825.675 1.5 1.5 1.5h15c.825 0 1.5-.675 1.5-1.5v-15c0-.825-.675-1.5-1.5-1.5zm0 16.5h-15V8.25h15v11.25zM7.5 10.5h3v3h-3v-3zm4.5 0h3v3h-3v-3zm4.5 0h3v3h-3v-3z"/>
  </svg>
);

const CalendarioPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [registrations, setRegistrations] = useState({});
  const [registering, setRegistering] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Función para generar link de Google Calendar
  const generateGoogleCalendarUrl = (event) => {
    const startDate = new Date(event.date);
    if (event.time) {
      const [hours, minutes] = event.time.split(':');
      startDate.setHours(parseInt(hours), parseInt(minutes));
    }
    const endDate = new Date(startDate.getTime() + (event.duration_hours || 2) * 60 * 60 * 1000);
    
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `UPIM: ${event.title}`,
      dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
      details: `${event.description || ''}\n\nEvento UPIM - Unión de Profesionales Inmobiliarios de México\n\nMás información: https://www.upim.org/calendario`,
      location: event.location || (event.event_type === 'online' ? 'Evento Online - Link se enviará por correo' : ''),
      ctz: 'America/Mexico_City'
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Función para descargar archivo ICS (compatible con cualquier calendario)
  const downloadICS = (event) => {
    const startDate = new Date(event.date);
    if (event.time) {
      const [hours, minutes] = event.time.split(':');
      startDate.setHours(parseInt(hours), parseInt(minutes));
    }
    const endDate = new Date(startDate.getTime() + (event.duration_hours || 2) * 60 * 60 * 1000);

    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '').slice(0, -1);
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//UPIM//Calendario de Eventos//ES
BEGIN:VEVENT
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:UPIM: ${event.title}
DESCRIPTION:${event.description || 'Evento UPIM'}\\n\\nMás información: https://www.upim.org/calendario
LOCATION:${event.location || (event.event_type === 'online' ? 'Online' : '')}
BEGIN:VALARM
TRIGGER:-PT1H
ACTION:DISPLAY
DESCRIPTION:Recordatorio: ${event.title} comienza en 1 hora
END:VALARM
BEGIN:VALARM
TRIGGER:-PT1D
ACTION:DISPLAY
DESCRIPTION:Recordatorio: ${event.title} es mañana
END:VALARM
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `UPIM-${event.title.replace(/[^a-zA-Z0-9]/g, '-')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Evento descargado",
      description: "El archivo .ics se descargó. Ábrelo para agregar el evento a tu calendario.",
    });
  };

  // Descargar todos los eventos
  const downloadAllEvents = () => {
    const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());
    
    if (upcomingEvents.length === 0) {
      toast({
        title: "Sin eventos",
        description: "No hay eventos próximos para descargar",
        variant: "destructive"
      });
      return;
    }

    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//UPIM//Calendario de Eventos//ES
X-WR-CALNAME:Eventos UPIM
X-WR-TIMEZONE:America/Mexico_City
`;

    upcomingEvents.forEach(event => {
      const startDate = new Date(event.date);
      if (event.time) {
        const [hours, minutes] = event.time.split(':');
        startDate.setHours(parseInt(hours), parseInt(minutes));
      }
      const endDate = new Date(startDate.getTime() + (event.duration_hours || 2) * 60 * 60 * 1000);

      const formatICSDate = (date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '').slice(0, -1);
      };

      icsContent += `BEGIN:VEVENT
DTSTART:${formatICSDate(startDate)}
DTEND:${formatICSDate(endDate)}
SUMMARY:UPIM: ${event.title}
DESCRIPTION:${event.description || 'Evento UPIM'}
LOCATION:${event.location || ''}
BEGIN:VALARM
TRIGGER:-PT1H
ACTION:DISPLAY
DESCRIPTION:Recordatorio UPIM
END:VALARM
END:VEVENT
`;
    });

    icsContent += 'END:VCALENDAR';

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'UPIM-Todos-Los-Eventos.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Calendario descargado",
      description: `Se descargaron ${upcomingEvents.length} eventos con recordatorios`,
    });
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (isAuthenticated && events.length > 0) {
      checkRegistrations();
    }
  }, [isAuthenticated, events]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistrations = async () => {
    const regs = {};
    for (const event of events) {
      try {
        const response = await api.get(`/events/${event.id}/my-registration`);
        if (response.data.registered) {
          regs[event.id] = response.data;
        }
      } catch (error) {
        // Not registered
      }
    }
    setRegistrations(regs);
  };

  const handleRegister = async (eventId) => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para registrarte a eventos",
        variant: "destructive"
      });
      return;
    }

    setRegistering(eventId);
    try {
      const response = await api.post(`/events/${eventId}/register`);
      setRegistrations(prev => ({
        ...prev,
        [eventId]: {
          registered: true,
          qr_code: response.data.qr_code,
          qr_data: response.data.qr_data
        }
      }));
      toast({
        title: "¡Registro exitoso!",
        description: "Te hemos registrado al evento. Revisa tu código QR.",
      });
      fetchEvents(); // Refresh to update spots
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo completar el registro",
        variant: "destructive"
      });
    } finally {
      setRegistering(null);
    }
  };

  const handleCancelRegistration = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}/register`);
      setRegistrations(prev => {
        const newRegs = {...prev};
        delete newRegs[eventId];
        return newRegs;
      });
      toast({
        title: "Registro cancelado",
        description: "Tu registro ha sido cancelado",
      });
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo cancelar el registro",
        variant: "destructive"
      });
    }
  };

  const filteredEvents = events.filter(event => {
    if (filterType === 'all') return true;
    return event.event_type === filterType;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha por confirmar';
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-gray-100 via-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-gray-200 text-gray-700 mb-4">Eventos UPIM</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Calendario de Eventos
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Capacitaciones, networking y eventos exclusivos para miembros UPIM
            </p>
            
            {/* Botón de sincronización principal */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={downloadAllEvents}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar Todos los Eventos
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de Sincronización con Google Calendar */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0 bg-white p-4 rounded-full shadow-md">
                  <GoogleCalendarIcon />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center justify-center md:justify-start gap-2">
                    <Bell className="h-5 w-5 text-blue-600" />
                    Sincroniza tu Calendario con UPIM
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Nunca te pierdas una Master Class, webinar o evento presencial. 
                    Agrega los eventos de UPIM a tu Google Calendar y recibe <strong>recordatorios automáticos</strong> en tu celular.
                  </p>
                  <div className="bg-white/70 rounded-lg p-4 text-sm text-gray-700">
                    <p className="font-semibold mb-2">¿Cómo funciona?</p>
                    <ol className="list-decimal list-inside space-y-1 text-left">
                      <li>Haz clic en <span className="text-blue-600 font-medium">"Agregar a Google Calendar"</span> en cualquier evento</li>
                      <li>Se abrirá Google Calendar con el evento pre-llenado</li>
                      <li>Confirma haciendo clic en <strong>"Guardar"</strong></li>
                      <li>¡Listo! Recibirás recordatorios 1 día antes y 1 hora antes del evento</li>
                    </ol>
                  </div>
                </div>
                <div className="flex-shrink-0 text-center">
                  <Button 
                    onClick={downloadAllEvents}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Descargar .ICS
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">Compatible con iPhone, Outlook y más</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="text-sm text-gray-500">Filtrar:</span>
            <div className="flex space-x-2">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'presencial', label: 'Presencial' },
                { value: 'online', label: 'Online' }
              ].map(filter => (
                <Button
                  key={filter.value}
                  variant={filterType === filter.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterType(filter.value)}
                  className={filterType === filter.value ? 'bg-gray-900' : ''}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay eventos programados</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredEvents.map(event => (
                <Card key={event.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Event Image */}
                      <div className="md:w-64 h-48 md:h-auto flex-shrink-0 bg-gray-200">
                        {event.image_url ? (
                          <img 
                            src={event.image_url} 
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Event Info */}
                      <div className="flex-1 p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge className={event.event_type === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                                {event.event_type === 'online' ? (
                                  <><Video className="h-3 w-3 mr-1" /> Online</>
                                ) : (
                                  <><Building className="h-3 w-3 mr-1" /> Presencial</>
                                )}
                              </Badge>
                              {event.price === 0 && (
                                <Badge className="bg-gray-100 text-gray-700">Gratis</Badge>
                              )}
                            </div>
                            
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                            <p className="text-gray-600 text-sm mb-4">{event.description}</p>

                            <div className="space-y-2 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                                {formatDate(event.date)}
                              </div>
                              {event.time && (
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-2 text-gray-400" />
                                  {event.time} hrs
                                </div>
                              )}
                              {event.location && (
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                                  {event.location}
                                </div>
                              )}
                              {event.total_spots && (
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 mr-2 text-gray-400" />
                                  {event.available_spots} lugares disponibles de {event.total_spots}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Registration Section */}
                          <div className="w-full md:w-auto flex flex-col items-center space-y-3">
                            {event.price > 0 && (
                              <div className="text-center">
                                <p className="text-2xl font-bold text-gray-900">${event.price.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">MXN</p>
                              </div>
                            )}

                            {registrations[event.id] ? (
                              <div className="text-center">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
                                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                                  <p className="text-sm font-medium text-green-700">¡Registrado!</p>
                                  {registrations[event.id].qr_code && (
                                    <div className="mt-3">
                                      <img 
                                        src={`data:image/png;base64,${registrations[event.id].qr_code}`}
                                        alt="QR Code"
                                        className="w-24 h-24 mx-auto"
                                      />
                                      <p className="text-xs text-gray-500 mt-1">Tu pase de entrada</p>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Botones de calendario para eventos registrados */}
                                <div className="flex flex-col gap-2 mb-3">
                                  <a
                                    href={generateGoogleCalendarUrl(event)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                  >
                                    <GoogleCalendarIcon />
                                    <span className="ml-2">Agregar a Google Calendar</span>
                                  </a>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadICS(event)}
                                    className="text-gray-600"
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    Descargar .ICS
                                  </Button>
                                </div>

                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleCancelRegistration(event.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Cancelar
                                </Button>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-2">
                                <Button
                                  className="bg-gray-900 hover:bg-gray-800 w-full md:w-auto"
                                  onClick={() => handleRegister(event.id)}
                                  disabled={registering === event.id || (event.available_spots !== null && event.available_spots <= 0)}
                                >
                                  {registering === event.id ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Registrando...</>
                                  ) : event.available_spots !== null && event.available_spots <= 0 ? (
                                    'Sin lugares'
                                  ) : (
                                    <><QrCode className="h-4 w-4 mr-2" /> Registrarme</>
                                  )}
                                </Button>
                                
                                {/* Botón de Google Calendar para ver evento */}
                                <a
                                  href={generateGoogleCalendarUrl(event)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  <GoogleCalendarIcon />
                                  <span className="ml-1">+ Google Calendar</span>
                                  <ExternalLink className="h-3 w-3 ml-1" />
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default CalendarioPage;
